import { useQuery, useMutation } from '@tanstack/react-query';
import { IntegrationAPI } from '@newo/api/gateway';
import type {
    ISetting,
    IConnectorData,
    IIntegration,
    IConnector
} from '@newo/api/gateway';
import { AxiosRequestConfig } from 'axios';
import axios from '@/utils/api/axiosInstance';
import {
    pickClientSecret,
    VIBE_AGENT_INTEGRATION_IDN
} from '@/hooks/useVibeBuilderRemote.utils';

export const integrationAPI = new IntegrationAPI(axios);

type IQueryKeys = {
    getIntegrationSettings: (integrationId: string) => [string, string];
    getIntegrationConnectors: (integrationId: string) => [string, string];
};
export const QUERY_KEYS: IQueryKeys = {
    getIntegrationSettings: (integrationId) => [
        'integration/settings',
        integrationId
    ],
    getIntegrationConnectors: (integrationId) => [
        'integration/connectors',
        integrationId
    ]
};

// GET integrations
export const useIntegrationsQuery = () => {
    return useQuery({
        queryKey: ['integrations'],
        queryFn: async () => integrationAPI.getIntegrations(),
        refetchOnWindowFocus: false
    });
};

//  GET integration settings
export const useIntegrationSettingsQuery = (integrationId: string) => {
    return useQuery({
        queryKey: QUERY_KEYS.getIntegrationSettings(integrationId),
        queryFn: async () =>
            integrationAPI.getIntegrationSettings(integrationId),
        refetchOnWindowFocus: false
    });
};

// GET integration connectors
export const useIntegrationConnectorsQuery = (integrationId: string) => {
    return useQuery({
        queryKey: QUERY_KEYS.getIntegrationConnectors(integrationId),
        queryFn: async () =>
            integrationAPI.getIntegrationConnectors(integrationId),
        refetchOnWindowFocus: false
    });
};

// GET integration connector idns
export const useIntegrationConnectorIdnsQuery = (
    integrationIdn: string,
    config?: AxiosRequestConfig
) => {
    return useQuery({
        queryKey: ['integration/connectors/idns', integrationIdn, config],
        queryFn: async () =>
            integrationAPI.getIntegrationConnectorIdns(integrationIdn, {
                ...config
            }),
        enabled: Boolean(integrationIdn)
    });
};

// GET connector settings
export const useConnectorSettingsQuery = (connectorId: string) => {
    return useQuery({
        queryKey: ['connector/settings', connectorId],
        queryFn: async () => integrationAPI.getConnectorSettings(connectorId),
        enabled: Boolean(connectorId),
        gcTime: 0,
        refetchOnWindowFocus: false
    });
};

// CHANGE integration settings
export const useChangeIntegrationSettingsMutation = () => {
    return useMutation({
        mutationFn: async ({
            integrationId,
            data
        }: {
            integrationId: string;
            data?: ISetting[];
        }) =>
            integrationAPI.changeIntegrationSettings(integrationId, data ?? [])
    });
};

// UPDATE connector
export const useUpdateConnectorMutation = () => {
    return useMutation({
        mutationFn: async ({
            connectorId,
            data
        }: {
            connectorId: string;
            data: { title?: string; idn?: string };
        }) => integrationAPI.updateConnector(connectorId, data)
    });
};

// DELETE connector
export const useDeleteConnectorMutation = () => {
    return useMutation({
        mutationFn: async (connectorId: string) =>
            integrationAPI.deleteConnector(connectorId)
    });
};

// LAUNCH connector
export const useLaunchConnectorMutation = () => {
    return useMutation({
        mutationFn: async (connectorId: string) =>
            integrationAPI.launchConnector(connectorId)
    });
};

// STOP connector
export const useStopConnectorMutation = () => {
    return useMutation({
        mutationFn: async (connectorId: string) =>
            integrationAPI.stopConnector(connectorId)
    });
};

// CHANGE connector settings
export const useChangeConnectorSettingsMutation = () => {
    return useMutation({
        mutationFn: async ({
            connectorId,
            data
        }: {
            connectorId: string;
            data: ISetting[];
        }) => integrationAPI.changeConnectorSettings(connectorId, data)
    });
};

// GET Integration connector info by IDN
export const useIntegrationConnectorByIdnQuery = (
    integrationIdn: string,
    connectorIdn: string
) => {
    return useQuery({
        queryKey: ['integration/connector', integrationIdn, connectorIdn],
        queryFn: async () =>
            integrationAPI.getIntegrationConnectorByIdn(
                integrationIdn,
                connectorIdn,
                { noErrorMessage: true }
            )
    });
};

// CREATE and RUN connector
const createAndRunConnector = async ({
    integration,
    data
}: {
    integration: IIntegration;
    data: IConnectorData;
}): Promise<IConnector> => {
    await integrationAPI.createConnector(integration.id, data);
    const connector = await integrationAPI.getIntegrationConnectorByIdn(
        integration.idn,
        data.connector_idn
    );
    await integrationAPI.launchConnector(connector.id);
    return connector;
};

export const useCreateAndRunConnectorMutation = () => {
    return useMutation({
        mutationFn: createAndRunConnector
    });
};

const MS_IN_SECOND = 1000;
const SECONDS_IN_MINUTE = 60;
const VIBE_BUILDER_CONFIG_STALE_MINUTES = 5;
const VIBE_BUILDER_CONFIG_STALE_TIME_MS =
    VIBE_BUILDER_CONFIG_STALE_MINUTES * SECONDS_IN_MINUTE * MS_IN_SECOND;

export const useVibeBuilderConfigQuery = (enabled: boolean) =>
    useQuery({
        queryKey: ['integration/vibe-builder/config'],
        queryFn: async () => {
            const connectors =
                await integrationAPI.getByIntegrationIdnConnectors(
                    VIBE_AGENT_INTEGRATION_IDN
                );

            const clientSecret = pickClientSecret(connectors);

            if (!clientSecret) {
                throw new Error(
                    'vibe_agent connector or client_secret is missing'
                );
            }

            return { clientSecret };
        },
        enabled,
        staleTime: VIBE_BUILDER_CONFIG_STALE_TIME_MS,
        refetchOnWindowFocus: false
    });
